import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PieChart, TrendingUp, Activity, Target, 
  Calculator, X, Wrench, ArrowRight 
} from 'lucide-react';
import BudgetSimulator from './tools/BudgetSimulator';
import FreedomCalculator from './tools/FreedomCalculator';
import HealthDiagnosis from './tools/HealthDiagnosis';
import GoalsPlanner from './tools/GoalsPlanner';
import '../styles/design-system.css';

const tools = [
  {
    id: 'budget',
    name: 'Simulador de Orçamento',
    description: 'Distribua sua renda em categorias usando princípios bíblicos de prosperidade',
    icon: PieChart,
    accent: 'blue',
    component: BudgetSimulator
  },
  {
    id: 'freedom',
    name: 'Calculadora de Liberdade',
    description: 'Descubra em quanto tempo você alcançará a independência financeira',
    icon: TrendingUp,
    accent: 'green',
    component: FreedomCalculator
  },
  {
    id: 'health',
    name: 'Diagnóstico Financeiro',
    description: 'Avalie sua saúde financeira e receba recomendações personalizadas',
    icon: Activity,
    accent: 'orange',
    component: HealthDiagnosis
  },
  {
    id: 'goals',
    name: 'Planejador de Metas',
    description: 'Defina suas metas e saiba exatamente quanto guardar por mês',
    icon: Target,
    accent: 'pink',
    component: GoalsPlanner
  }
];

const FinancialToolsSection = () => {
  const [activeTool, setActiveTool] = useState(null);

  const handleOpenTool = (toolId) => {
    setActiveTool(toolId);
  };

  const handleCloseTool = () => {
    setActiveTool(null);
    // Scroll to contact section
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const ActiveComponent = activeTool 
    ? tools.find(t => t.id === activeTool)?.component 
    : null;

  return (
    <>
      <section 
        id="tools"
        className="py-20 md:py-32"
        style={{ background: 'var(--bg-page)' }}
      >
        <div className="container">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{ background: 'var(--accent-purple-200)' }}
            >
              <Wrench size={18} style={{ color: 'var(--accent-purple-400)' }} />
              <span className="mono-text">Ferramentas Gratuitas</span>
            </div>
            
            <h2 className="heading-hero mb-4">
              Ferramentas Financeiras
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ferramentas práticas e gratuitas para você organizar suas finanças 
              com base em princípios bíblicos de prosperidade
            </p>
          </motion.div>

          {/* Tools Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {tools.map((tool, index) => {
              const IconComponent = tool.icon;
              return (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`voice-card accent-${tool.accent} cursor-pointer group`}
                  onClick={() => handleOpenTool(tool.id)}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      <IconComponent size={28} style={{ color: 'var(--text-primary)' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="voice-card-title group-hover:opacity-80 transition-opacity">
                        {tool.name}
                      </h3>
                      <p className="voice-card-description">
                        {tool.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="caption">Usar ferramenta</span>
                    <ArrowRight size={16} />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="body-small mb-4" style={{ color: 'var(--text-muted)' }}>
              Quer resultados ainda melhores?
            </p>
            <a
              href="#contact"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Calculator size={18} />
              Agende uma Consultoria Personalizada
            </a>
          </motion.div>
        </div>
      </section>

      {/* Tool Modal */}
      <AnimatePresence>
        {activeTool && ActiveComponent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.6)' }}
            onClick={() => setActiveTool(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl relative"
              style={{ background: 'var(--bg-card)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setActiveTool(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10 hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>

              {/* Tool Content */}
              <ActiveComponent onClose={handleCloseTool} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FinancialToolsSection;
